import { Clickable } from '../components/Clickable';
import { Draggable } from '../components/Draggable';
import { SpriteRenderer } from '../components/SpriteRenderer';
import { type EngineContext } from '../core/EngineContext';
import { type EntityPointerEventPayload } from '../core/EngineEvents';
import { SystemStage, type System } from '../core/System';
import { type Entity } from '../entities/Entity';
import { type EntityId } from '../entities/EntityId';
import { WorldTransform } from '../entities/WorldTransform';
import { type InputManager } from '../input/InputManager';
import { type PointerState } from '../input/PointerState';
import { Vector2 } from '../math/Vector2';
import { fillSpriteQuad, spriteQuadContainsPoint, type SpriteQuad } from '../rendering/SpriteQuad';
import { type Scene } from '../scene/Scene';

/** A candidate under the pointer, kept with the two fields that decide which one wins. */
interface PointerCandidate {
  readonly entity: Entity;
  readonly sortOrder: number;
}

/**
 * Works out what the pointer is touching and says so, once per frame.
 *
 * A system rather than a behavior because the answer depends on every interactive entity at once:
 * "which of these did the toddler mean" is a question about the whole scene, and only the topmost
 * sprite may win. Front-most wins, using the same order the renderer draws in, so what looks on top is
 * what responds.
 *
 * It emits and never acts. Press, release, click and drag go out as engine events carrying entity ids,
 * and what any of them means is a behavior's decision. That is what keeps `Clickable` a component with
 * no callback in it.
 */
export class PointerInteractionSystem implements System {
  /** Hit-testing runs in the input stage, after the pointer state for the frame exists. */
  readonly stage = SystemStage.Input;

  readonly #input: InputManager;
  readonly #worldTransform = new WorldTransform();
  readonly #quad: SpriteQuad = { x: 0, y: 0, rotation: 0, width: 0, height: 0, anchorX: 0, anchorY: 0 };
  readonly #dragOrigin = Vector2.zero();
  readonly #dragDelta = Vector2.zero();

  #events: EngineContext['events'] | undefined;
  #pressedEntityId: EntityId | undefined;
  #draggedEntityId: EntityId | undefined;

  constructor(input: InputManager) {
    this.#input = input;
  }

  /** Captures the event bus the interactions are published on. */
  onRegister(context: EngineContext): void {
    this.#events = context.events;
  }

  /** Emits everything the pointer did to the scene this frame. */
  onUpdate(scene: Scene, _deltaSeconds: number): void {
    const pointer = this.#input.pointer;

    if (pointer.pressedThisFrame) {
      this.#beginPress(scene, pointer);
    }
    this.#updateDrag(scene, pointer);
    if (pointer.releasedThisFrame) {
      this.#endPress(scene, pointer);
    }
  }

  /** Forgets any press or drag in progress, so a removed system leaves nothing half held. */
  onUnregister(): void {
    this.#pressedEntityId = undefined;
    this.#draggedEntityId = undefined;
    this.#events = undefined;
  }

  #beginPress(scene: Scene, pointer: PointerState): void {
    const entity = this.#pick(scene, pointer.pressWorldPosition);
    if (entity === undefined) {
      return;
    }

    this.#pressedEntityId = entity.id;
    this.#events?.emit('entityPressed', this.#pressPayload(entity.id, pointer));

    if (isEnabled(entity.getComponent(Draggable))) {
      this.#draggedEntityId = entity.id;
      this.#dragOrigin.copyFrom(pointer.pressWorldPosition);
      this.#events?.emit('dragStarted', { ...this.#pressPayload(entity.id, pointer), delta: Vector2.zero() });
    }
  }

  /*
   * On the frame a drag ends, the pointer has already stopped being down, so the travel between the
   * last frame and the release would otherwise be dropped. Measuring to the release point instead is
   * what makes a quick flick move the thing it grabbed, rather than registering as a drag that went
   * nowhere.
   */
  #updateDrag(scene: Scene, pointer: PointerState): void {
    const draggedId = this.#draggedEntityId;
    if (draggedId === undefined) {
      return;
    }

    if (scene.getEntity(draggedId) === undefined) {
      this.#draggedEntityId = undefined;
      return;
    }
    if (!pointer.isDown && !pointer.releasedThisFrame) {
      return;
    }

    const current = pointer.releasedThisFrame ? pointer.releaseWorldPosition : pointer.worldPosition;
    this.#dragDelta.copyFrom(current).subtract(this.#dragOrigin);
    if (this.#dragDelta.x === 0 && this.#dragDelta.y === 0) {
      return;
    }

    this.#dragOrigin.copyFrom(current);
    this.#events?.emit('dragMoved', {
      ...this.#pointerPayload(draggedId, pointer.screenPosition, current),
      delta: this.#dragDelta.clone(),
    });
  }

  #endPress(scene: Scene, pointer: PointerState): void {
    const pressedId = this.#pressedEntityId;
    const draggedId = this.#draggedEntityId;
    this.#pressedEntityId = undefined;
    this.#draggedEntityId = undefined;

    if (draggedId !== undefined && scene.getEntity(draggedId) !== undefined) {
      this.#events?.emit('dragEnded', { ...this.#releasePayload(draggedId, pointer), delta: Vector2.zero() });
    }

    if (pressedId === undefined) {
      return;
    }
    const pressed = scene.getEntity(pressedId);
    if (pressed === undefined) {
      return;
    }

    this.#events?.emit('entityReleased', this.#releasePayload(pressedId, pointer));

    const released = this.#pick(scene, pointer.releaseWorldPosition);
    if (released?.id === pressedId && isEnabled(pressed.getComponent(Clickable))) {
      this.#events?.emit('entityClicked', this.#releasePayload(pressedId, pointer));
    }
  }

  /*
   * Both interactive component types are gathered rather than one, so an entity that is only draggable
   * still blocks a clickable behind it. Anything else would let the toddler press what they can see
   * through.
   */
  #pick(scene: Scene, worldPoint: Vector2): Entity | undefined {
    let best: PointerCandidate | undefined;

    for (const entity of collectInteractive(scene)) {
      const padding = hitPadding(entity);
      if (padding === undefined || !entity.activeInHierarchy) {
        continue;
      }

      const sprite = entity.getComponent(SpriteRenderer);
      if (!sprite?.visible) {
        continue;
      }

      entity.getWorldTransform(this.#worldTransform);
      fillSpriteQuad(this.#quad, sprite, this.#worldTransform);
      if (!spriteQuadContainsPoint(this.#quad, worldPoint.x, worldPoint.y, padding)) {
        continue;
      }

      if (best === undefined || isInFront(sprite.sortOrder, entity.id, best)) {
        best = { entity, sortOrder: sprite.sortOrder };
      }
    }

    return best?.entity;
  }

  #pointerPayload(entityId: EntityId, screen: Vector2, world: Vector2): EntityPointerEventPayload {
    return { entityId, screenPosition: screen.clone(), worldPosition: world.clone() };
  }

  #pressPayload(entityId: EntityId, pointer: PointerState): EntityPointerEventPayload {
    return this.#pointerPayload(entityId, pointer.pressPosition, pointer.pressWorldPosition);
  }

  #releasePayload(entityId: EntityId, pointer: PointerState): EntityPointerEventPayload {
    return this.#pointerPayload(entityId, pointer.releasePosition, pointer.releaseWorldPosition);
  }
}

/** Every entity carrying either interactive component, without listing an entity that has both twice. */
function collectInteractive(scene: Scene): readonly Entity[] {
  const clickable = scene.getEntitiesWith(Clickable);
  const draggable = scene.getEntitiesWith(Draggable);
  if (draggable.length === 0) {
    return clickable;
  }
  if (clickable.length === 0) {
    return draggable;
  }
  return [...new Set([...clickable, ...draggable])];
}

/** The hit padding an entity's interactive components ask for, or undefined when none is enabled. */
function hitPadding(entity: Entity): number | undefined {
  const clickable = entity.getComponent(Clickable);
  const draggable = entity.getComponent(Draggable);
  const paddings = [clickable, draggable].filter(isEnabled).map((component) => component.padding);
  return paddings.length === 0 ? undefined : Math.max(...paddings);
}

function isEnabled(component: Clickable | Draggable | undefined): component is Clickable | Draggable {
  return component?.enabled ?? false;
}

function isInFront(sortOrder: number, entityId: EntityId, best: PointerCandidate): boolean {
  if (sortOrder !== best.sortOrder) {
    return sortOrder > best.sortOrder;
  }
  return entityId > best.entity.id;
}
